import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Tabs,
    Tab,
    Button,
    CircularProgress,
    Snackbar,
    Alert,
    Divider,
} from '@mui/material';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
    getLegalContent,
    upsertLegalContent,
    UserType,
    DocType
} from '../../services/legalContentService';

const LegalContent = () => {
    const [searchParams] = useSearchParams();

    // Initialize from URL param or default to JOB_SEEKER
    // If 'type' param is 'HIRING_MANAGER', use HIRING_MANAGER, else default to JOB_SEEKER
    const type = searchParams.get('type') === 'HIRING_MANAGER'
        ? UserType.HIRING_MANAGER
        : UserType.JOB_SEEKER;

    const [docType, setDocType] = useState<DocType>(DocType.TERMS);
    const [content, setContent] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const fetchContent = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await getLegalContent(type, docType);
            setContent(result.data.content);
        } catch (err: any) {
            // If 404, it just means no content yet, which is fine
            if (err.response && err.response.status === 404) {
                setContent('');
            } else {
                setError('Failed to fetch content');
                console.error(err);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchContent();
        // eslint-disable-next-line
    }, [type, docType]);

    const handleDocTypeChange = (event: React.SyntheticEvent, newValue: DocType) => {
        setDocType(newValue);
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            await upsertLegalContent(type, docType, content);
            setSuccess('Content saved successfully');
        } catch (err: any) {
            setError('Failed to save content');
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box>
            <Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold' }}>
                Legal & Compliance
            </Typography>
            <Typography variant="subtitle1" sx={{ mb: 4, color: 'text.secondary', fontWeight: 'medium' }}>
                {type === UserType.HIRING_MANAGER ? 'For Hiring Managers' : 'For Job Seekers'}
            </Typography>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ mb: 3 }}>
                    <Tabs
                        value={docType}
                        onChange={handleDocTypeChange}
                        textColor="primary"
                        indicatorColor="primary"
                        aria-label="document type tabs"
                    >
                        <Tab label="Terms & Conditions" value={DocType.TERMS} />
                        <Tab label="Privacy Policy" value={DocType.PRIVACY} />
                    </Tabs>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box className="quill-editor-container" sx={{ height: '500px', mb: 8 }}>
                        <ReactQuill
                            theme="snow"
                            value={content}
                            onChange={setContent}
                            style={{ height: '450px' }}
                            modules={{
                                toolbar: [
                                    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                                    ['bold', 'italic', 'underline', 'strike'],
                                    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                    [{ 'align': [] }],
                                    ['link'],
                                    ['clean']
                                ],
                            }}
                        />
                    </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                        disabled={saving || loading}
                        size="large"
                    >
                        {saving ? 'Saving...' : 'Save Content'}
                    </Button>
                </Box>
            </Paper>

            <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
                <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>

            <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess(null)}>
                <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
                    {success}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default LegalContent;
